import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../app/theme';
import {
  GPTModelType,
  GPTModelTypePoints,
  ModalType,
  ModelProvider,
} from '../../../app/types';
import { closeModal, openModal } from '../../../features/modalSlice';
import Dropdown from '../../shared/Dropdown';
import RowTitle from '../RowTitle';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      display: 'flex',
      alignItems: 'baseline',
    },
    learnModel: {
      marginBottom: 12,
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '13px',
      textDecorationLine: 'underline',
      color: color.$1,
      marginLeft: 4,
      cursor: 'pointer',
    },
    learnModelContent: {
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$3,
    },
    learnModelContentTitle: {
      fontSize: '17px',
      fontWeight: '500',
      marginBottom: 6,
    },
    hr: {
      height: 1,
      width: '100%',
      backgroundColor: color.$9,
      margin: '12px 0',
    },
    dropdown: {
      width: 260,
    },
  },
  {
    name: 'Model',
  },
);

const Model = ({ model = '', onChange, disabled }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { pathKey } = useSelector(store => store.aiBot);

  const GPTModelTypes = useMemo(() => {
    return Object.values(GPTModelType);
  }, []);

  const handleChange = v => {
    dispatch(
      openModal({
        modalType: ModalType.ALERT_MODAL,
        modalProps: {
          title: t(`${pathKey}:basicSettingContainer.${v}.learnModelTitle`),
          content: (
            <div className={classes.learnModelContent}>
              <div className={classes.learnModelContentTitle}>
                {t(`${pathKey}:basicSettingContainer.learnModelContentTitle`)}
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: t(
                    `${pathKey}:basicSettingContainer.learnModelContent`,
                  ),
                }}
              />
              <div
                style={{ marginTop: 12 }}
                className={classes.learnModelContentTitle}
              >
                {t(`${pathKey}:basicSettingContainer.learnModelContentTitle1`)}
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: t(
                    `${pathKey}:basicSettingContainer.learnModelContent1`,
                  ),
                }}
              />
              <LanguageModelTable />
            </div>
          ),
          onCancel: () => {
            dispatch(closeModal());
          },
          onClick: async () => {
            onChange({ model: v, provider: ModelProvider[v] });
            dispatch(closeModal());
          },
          cancelText: t(`${pathKey}:basicSettingContainer.cancel`),
          submitText: t(`${pathKey}:basicSettingContainer.${v}.changeTo`),
        },
      }),
    );
  };

  const handleLearnModel = () => {
    dispatch(
      openModal({
        modalType: ModalType.ALERT_MODAL,
        modalProps: {
          title: t(`${pathKey}:basicSettingContainer.learnModelTitle`),
          content: (
            <div className={classes.learnModelContent}>
              <div className={classes.learnModelContentTitle}>
                {t(`${pathKey}:basicSettingContainer.learnModelContentTitle`)}
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: t(
                    `${pathKey}:basicSettingContainer.learnModelContent`,
                  ),
                }}
              />
              <div
                style={{ marginTop: 12 }}
                className={classes.learnModelContentTitle}
              >
                {t(`${pathKey}:basicSettingContainer.learnModelContentTitle1`)}
              </div>
              <div
                dangerouslySetInnerHTML={{
                  __html: t(
                    `${pathKey}:basicSettingContainer.learnModelContent1`,
                  ),
                }}
              />
              <LanguageModelTable />
            </div>
          ),
          onClick: () => {
            dispatch(closeModal());
          },
          submitText: t(`${pathKey}:basicSettingContainer.understand`),
        },
      }),
    );
  };

  const modelItems = GPTModelTypes.map(value => ({
    text: t(`${value}`),
    value,
  }));

  return (
    <div className={classes.root}>
      <div className={classes.title}>
        <RowTitle
          style={{ marginBottom: 0 }}
          title={t(`${pathKey}:basicSettingContainer.model`)}
          isRequired
        />
        <div className={classes.learnModel} onClick={handleLearnModel}>
          {t(`${pathKey}:basicSettingContainer.learnModel`)}
        </div>
      </div>
      <Dropdown
        className={classes.dropdown}
        menuItems={modelItems}
        value={model}
        onChange={v => handleChange(v)}
        disabled={disabled}
      />
    </div>
  );
};

const tableStyle = {
  borderCollapse: 'separate',
  borderSpacing: 0,
  borderRadius: '8px',
  overflow: 'hidden',
  marginTop: '8px',
};

const tableHeaderStyle = {
  backgroundColor: 'white',
  padding: '8px',
  textAlign: 'left',
  whiteSpace: 'nowrap',
  borderBottom: '1px solid #E0E0E0',
  color: '#989898',
  fontSize: '13px',
  lineHeight: '40px',
};

const tableCellStyle = {
  backgroundColor: 'white',
  padding: '8px',
  whiteSpace: 'nowrap',
  color: '#4c4c4c',
  fontSize: '15px',
  lineHeight: '40px',
};

const LanguageModelTable = () => {
  const { t } = useTranslation();
  const { pathKey } = useSelector(store => store.aiBot);

  const data = useMemo(() => {
    return Object.keys(GPTModelType).map(type => ({
      model: t(`${GPTModelType[type]}`),
      points: GPTModelTypePoints[type],
    }));
  }, []);

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <th style={tableHeaderStyle}>
            {t(`${pathKey}:basicSettingContainer.learnModelContentTitle`)}
          </th>
          <th style={{ ...tableHeaderStyle, borderLeft: '1px solid #E0E0E0' }}>
            {t(`${pathKey}:basicSettingContainer.learnModelContentTitle1`)}
          </th>
        </tr>
      </thead>
      <tbody>
        {data.map((row, index) => (
          <tr key={index}>
            <td style={tableCellStyle}>{row.model}</td>
            <td style={{ ...tableCellStyle, borderLeft: '1px solid #E0E0E0' }}>
              {row.points}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default Model;
